import {useContext} from 'react'
import BarChart from '../../../../components/charts/BarChart'
import PieChart from '../../../../components/charts/PieChart'
import Tooltip from '../../../../components/Tooltip/Tooltip'
import {formatPrice} from '../../../../utils/common'
import {
  BarChartData,
  formatPieChartData,
  getBarChartOptions,
  mapCustomerGroups,
  PieCharData,
  pieChartOptions,
  transformToBarChartData,
} from '../../utils'
import {ProfitabilityContext} from '../../context'
import StateBox from '../../components/StateBox'
import OrderBox from '../../components/OrderBox'
import ChartStateBox from '../../components/ChartStateBox'

const ProfitabilityDashboardCharts = () => {
  const {
    isFullView,
    dateRangeLable,
    profitabilitySummaryData,
    isProfitabilitySummaryLoading,
    customersGroupsChartData,
    isCustomersGroupsChartLoading,
    ordersOverviewData,
  } = useContext(ProfitabilityContext)

  if (isFullView) return null

  const {customer_type_metrics, top_20_percent, total_metrics} = profitabilitySummaryData
  const pieChartDataValues = formatPieChartData(total_metrics || {})
  const barChartDataValues = transformToBarChartData(customersGroupsChartData)
  const mappedCustomerGroups = mapCustomerGroups(customersGroupsChartData)

  return (
    <div className='row g-xl-custom-7 mb-10'>
      {/* Top Customer Group */}
      <ChartStateBox
        title='Top Customer Group'
        description={customersGroupsChartData?.[0]?.customer_group_name}
        isLoading={isCustomersGroupsChartLoading}
        chart={
          <BarChart
            data={BarChartData(barChartDataValues)}
            options={getBarChartOptions(mappedCustomerGroups)}
            height={300}
          />
        }
      />

      {/* Profitability Overview */}
      <ChartStateBox
        title='Profitability'
        description={dateRangeLable}
        isLoading={isProfitabilitySummaryLoading}
        cardBodyClass='profibility-chart-wrapper w-100'
        chart={
          <div className='w-100 d-flex justify-content-center'>
            <div className='col-xxl-7 col-xl-12 col-lg-12 position-relative'>
              <PieChart data={PieCharData(pieChartDataValues)} options={pieChartOptions} />
              <div className='position-absolute top-50 start-50 translate-middle text-center'>
                <p className='fs-1 fw-bold mb-0'>
                  <Tooltip
                    text={total_metrics?.revenue_label || 0}
                    tooltipText={formatPrice(total_metrics?.revenue, false)}
                  />
                </p>
                <span className='fw-semibold text-gray-500'>Total Revenue</span>
              </div>
            </div>
          </div>
        }
        footer={
          <div className='w-100 mt-5'>
            <div className='row'>
              <div className='col-6'>
                <h3 className='mb-0 fs-4'>Cost</h3>
                <span className='fs-6 fw-semibold text-gray-500'>
                  {formatPrice(total_metrics?.cost, false)}
                </span>
              </div>
              <div className='col-6 text-end'>
                <h3 className='mb-0 fs-4'>Profit</h3>
                <span className='fs-6 fw-semibold text-gray-500'>
                  {formatPrice(total_metrics?.profit, false)}
                </span>
              </div>
            </div>
          </div>
        }
      />

      {/* Revenue Stats Boxes Starts */}
      <div className='col-xl-4 mt-0'>
        <div className='row g-xl-custom-7'>
          {/* Top 20% Revenue by customers */}
          <StateBox
            semiHeader='Top 20% Revenue'
            header={`${top_20_percent?.revenue_percentage || 0}%`}
            description={`${top_20_percent?.customer_count} Customers`}
          />

          {/* Top 20% Revenue by orders */}
          <StateBox
            semiHeader='Top 20% Revenue'
            header={`$${top_20_percent?.revenue_label || 0}`}
            tooltip={formatPrice(top_20_percent?.revenue, false)}
            description={`${top_20_percent?.orders} Orders`}
          />

          {/* Chain Store & Retailer */}
          <StateBox
            semiHeader={`${customer_type_metrics?.other?.revenue_percentage || 0}%`}
            header={`$${customer_type_metrics?.other?.revenue_label || 0}`}
            tooltip={formatPrice(customer_type_metrics?.other?.revenue, false)}
            description='Chain Store & Retailer'
          />

          {/* Distributor */}
          <StateBox
            semiHeader={`${customer_type_metrics?.distributor?.revenue_percentage || 0}%`}
            header={`$${customer_type_metrics?.distributor?.revenue_label || 0}`}
            tooltip={formatPrice(customer_type_metrics?.distributor?.revenue, false)}
            description='Distributor'
          />
        </div>
      </div>
      {/* Revenue Stats Boxes Ends */}

      <div className='mt-10'>
        <div className='d-flex gap-5 flex-wrap'>
          {ordersOverviewData && (
            <>
              {[
                {
                  semiHeader: 'Order 20%+ Profit',
                  dataKey: '20_plus',
                  className: 'bg-success bg-opacity-25',
                },
                {
                  semiHeader: 'Order 15 to 20% Profit',
                  dataKey: '15_to_20',
                  className: 'bg-success bg-opacity-20',
                },
                {
                  semiHeader: 'Order 10 to 15% Profit',
                  dataKey: '10_to_15',
                  className: 'bg-success bg-opacity-15',
                },
                {
                  semiHeader: 'Order 5 to 10% Profit',
                  dataKey: '5_to_10',
                  className: 'bg-success bg-opacity-10',
                },
                {
                  semiHeader: 'Order 0 to 5% Profit',
                  dataKey: '0_to_5',
                  className: 'bg-success bg-opacity-5',
                },
                {
                  semiHeader: 'Order 0% Profit',
                  dataKey: '0',
                  className: 'bg-light-secondary',
                },
                {
                  semiHeader: 'Order 0 to 5% Loss',
                  dataKey: '-5_to_0',
                  className: 'bg-danger bg-opacity-5',
                },
                {
                  semiHeader: 'Order 5 to 10% Loss',
                  dataKey: '-10_to_-5',
                  className: 'bg-danger bg-opacity-10',
                },
                {
                  semiHeader: 'Order 10 to 15% Loss',
                  dataKey: '-15_to_-10',
                  className: 'bg-danger bg-opacity-15',
                },
                {
                  semiHeader: 'Order 15 to 20% Loss',
                  dataKey: '-20_to_-15',
                  className: 'bg-danger bg-opacity-20',
                },
                {
                  semiHeader: 'Order 20%+ Loss',
                  dataKey: '-20_plus',
                  className: 'bg-danger bg-opacity-25',
                },
              ].map((item, index) => (
                <OrderBox
                  key={index}
                  className={item.className}
                  semiHeader={item.semiHeader}
                  headerLink='/analytics/profitability/profitability-report'
                  searchParams={`?view=order-report&profit_percentage=${item.dataKey}`}
                  header={ordersOverviewData?.[item.dataKey]?.toString() || '0'}
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ProfitabilityDashboardCharts
